/**
 * Meu perfil — dados, foto, senha, MFA e sessões.
 *
 * O que a pessoa muda em si mesma vive aqui, e o que um administrador muda em
 * terceiros vive em Usuários. A divisão não é organizacional: em Usuários toda
 * ação pergunta "posso mexer nesta pessoa?" e a resposta correta para si mesmo
 * é *não* — é isso que impede ampliar o próprio acesso ou se desligar sem
 * volta. Nome, cargo e foto, porém, cada um corrige sem pedir a ninguém.
 *
 * Papel, organização e situação da conta NÃO aparecem como campos editáveis
 * aqui. Se aparecessem, a divisão acima não teria servido para nada.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import { api, type Item, type Lista } from '../api/cliente'
import { Avatar, esquecerFoto, iniciaisDe } from '../componentes/Avatar'
import { Aviso, Botao, Campo, Carregando, Cartao, Etiqueta, formatarData } from '../componentes/ui'
import { useAuth } from '../estado/auth'

interface PapelDoPerfil {
  id: string
  papel: string
  organizacaoId: string | null
  organizacaoNome: string | null
  criadoEm: string
}

interface Perfil {
  id: string
  nome: string
  email: string
  telefone: string | null
  cargo: string | null
  setor: string | null
  temFoto: boolean
  mfaAtivo: boolean
  mfaObrigatorio: boolean
  ultimoLoginEm: string | null
  criadoEm: string
  papeis: PapelDoPerfil[]
  papelAtivo: string
}

interface Sessao {
  id: string
  ip: string | null
  userAgent: string | null
  criadoEm: string
  expiraEm: string
}

const ROTULO_PAPEL: Record<string, string> = {
  admin: 'Administrador técnico',
  admin_master_egba: 'Administrador master EGBA',
  egba: 'Equipe EGBA',
  parceiro: 'Parceiro',
  cliente: 'Cliente (titular)',
  operador: 'Operador',
  leitura: 'Leitura / auditoria',
}

export function Perfil() {
  const qc = useQueryClient()
  const { usuario: sessao } = useAuth()
  const arquivo = useRef<HTMLInputElement>(null)

  const [erro, setErro] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [rascunho, setRascunho] = useState<Partial<Perfil> | null>(null)
  const [senhas, setSenhas] = useState({ atual: '', nova: '', repetir: '' })
  // Muda a cada troca de foto para forçar o Avatar a remontar e buscar de novo.
  const [versaoFoto, setVersaoFoto] = useState(0)

  const { data, isLoading } = useQuery({
    queryKey: ['perfil'],
    queryFn: () => api.get<Item<Perfil>>('/api/v1/perfil'),
  })

  const { data: sessoes } = useQuery({
    queryKey: ['perfil-sessoes'],
    queryFn: () => api.get<Lista<Sessao>>('/api/v1/perfil/sessoes'),
  })

  const eu = data?.dados
  const campo = <K extends keyof Perfil>(k: K): Perfil[K] | undefined =>
    (rascunho?.[k] as Perfil[K] | undefined) ?? eu?.[k]

  const salvar = useMutation({
    mutationFn: () =>
      api.patch<Item<Perfil>>('/api/v1/perfil', {
        nome: campo('nome'),
        telefone: campo('telefone') ?? null,
        cargo: campo('cargo') ?? null,
        setor: campo('setor') ?? null,
      }),
    onSuccess: () => {
      setRascunho(null)
      setErro(null)
      setAviso('Dados atualizados.')
      void qc.invalidateQueries({ queryKey: ['perfil'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  const enviarFoto = useMutation({
    mutationFn: (f: File) => api.enviarArquivo('/api/v1/perfil/foto', f, 'foto'),
    onSuccess: () => {
      if (eu) esquecerFoto(eu.id)
      setVersaoFoto((v) => v + 1)
      setErro(null)
      setAviso('Foto atualizada.')
      void qc.invalidateQueries({ queryKey: ['perfil'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  const removerFoto = useMutation({
    mutationFn: () => api.delete('/api/v1/perfil/foto'),
    onSuccess: () => {
      if (eu) esquecerFoto(eu.id)
      setVersaoFoto((v) => v + 1)
      setAviso('Foto removida.')
      void qc.invalidateQueries({ queryKey: ['perfil'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  const trocarSenha = useMutation({
    mutationFn: () =>
      api.post<{ mensagem?: string }>('/api/v1/auth/senha', {
        senhaAtual: senhas.atual,
        novaSenha: senhas.nova,
      }),
    onSuccess: (r) => {
      setSenhas({ atual: '', nova: '', repetir: '' })
      setErro(null)
      // A troca derruba todas as sessões, inclusive esta — dizer isso antes de
      // a tela de login aparecer evita a leitura de "deu erro e me deslogou".
      setAviso(r.mensagem ?? 'Senha alterada. Entre novamente.')
    },
    onError: (e: Error) => setErro(e.message),
  })

  const encerrarSessao = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/perfil/sessoes/${id}`),
    onSuccess: () => {
      setAviso('Sessão encerrada.')
      void qc.invalidateQueries({ queryKey: ['perfil-sessoes'] })
    },
    onError: (e: Error) => setErro(e.message),
  })

  if (isLoading || !eu) return <Carregando />

  const sujo = rascunho !== null
  const senhaPronta =
    senhas.atual.length > 0 && senhas.nova.length >= 12 && senhas.nova === senhas.repetir

  return (
    <div className="space-y-5">
      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {aviso && !erro && <Aviso tom="sucesso">{aviso}</Aviso>}

      {/* ------------------------------------------------------------- foto */}
      <Cartao titulo="Meus dados" descricao="Como você aparece para o resto da equipe.">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="flex flex-col items-center gap-3">
            <Avatar
              key={versaoFoto}
              usuarioId={eu.id}
              nome={eu.nome}
              temFoto={eu.temFoto}
              tamanho={112}
              className="ring-4 ring-white shadow-md"
            />
            <input
              ref={arquivo}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/avif"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) enviarFoto.mutate(f)
                e.target.value = ''
              }}
            />
            <div className="flex gap-2">
              <Botao
                variante="secundario"
                tamanho="sm"
                carregando={enviarFoto.isPending}
                onClick={() => arquivo.current?.click()}
              >
                {eu.temFoto ? 'Trocar' : 'Enviar foto'}
              </Botao>
              {eu.temFoto && (
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  carregando={removerFoto.isPending}
                  onClick={() => removerFoto.mutate()}
                >
                  Remover
                </Botao>
              )}
            </div>
            <p className="max-w-[180px] text-center text-[11px] leading-snug text-slate-400">
              A imagem é recortada em quadrado e os metadados da câmera são descartados no envio.
            </p>
          </div>

          <div className="grid flex-1 gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Nome completo"
              value={campo('nome') ?? ''}
              onChange={(e) => setRascunho({ ...rascunho, nome: e.target.value })}
            />
            <Campo
              rotulo="E-mail"
              value={eu.email}
              disabled
              ajuda="É a sua identificação de acesso. Para trocar, fale com um administrador."
            />
            <Campo
              rotulo="Cargo"
              placeholder="Diretora de Comunicação"
              value={campo('cargo') ?? ''}
              onChange={(e) => setRascunho({ ...rascunho, cargo: e.target.value })}
            />
            <Campo
              rotulo="Setor"
              placeholder="Gabinete"
              value={campo('setor') ?? ''}
              onChange={(e) => setRascunho({ ...rascunho, setor: e.target.value })}
            />
            <Campo
              rotulo="Telefone"
              value={campo('telefone') ?? ''}
              onChange={(e) => setRascunho({ ...rascunho, telefone: e.target.value })}
            />
            <div className="flex items-end">
              <Botao disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {sujo ? 'Salvar alterações' : 'Salvo'}
              </Botao>
            </div>
          </div>
        </div>
      </Cartao>

      {/* ----------------------------------------------------------- acesso */}
      <Cartao titulo="Meu acesso" descricao="Papéis concedidos a você e onde eles valem.">
        <ul className="divide-y divide-slate-100 rounded-lg border border-[color:var(--color-borda)]">
          {eu.papeis.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-4 px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">
                  {ROTULO_PAPEL[p.papel] ?? p.papel}
                  {p.papel === eu.papelAtivo && (
                    <span className="ml-2">
                      <Etiqueta tom="marca">em uso nesta sessão</Etiqueta>
                    </span>
                  )}
                </p>
                <p className="truncate text-xs text-slate-500">
                  {p.organizacaoNome ?? 'Alcance global'} · desde {formatarData(p.criadoEm)}
                </p>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          Papéis são concedidos por outro administrador, nunca por você mesmo — é o que impede
          alguém de ampliar o próprio acesso.
        </p>
      </Cartao>

      {/* ------------------------------------------------------------ senha */}
      <Cartao
        titulo="Senha e verificação em duas etapas"
        descricao="Trocar a senha encerra todas as suas sessões, inclusive esta."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <Campo
            rotulo="Senha atual"
            type="password"
            autoComplete="current-password"
            value={senhas.atual}
            onChange={(e) => setSenhas({ ...senhas, atual: e.target.value })}
          />
          <Campo
            rotulo="Nova senha"
            type="password"
            autoComplete="new-password"
            value={senhas.nova}
            onChange={(e) => setSenhas({ ...senhas, nova: e.target.value })}
            ajuda="Mínimo de 12 caracteres."
          />
          <Campo
            rotulo="Repita a nova senha"
            type="password"
            autoComplete="new-password"
            value={senhas.repetir}
            onChange={(e) => setSenhas({ ...senhas, repetir: e.target.value })}
            ajuda={
              senhas.repetir && senhas.nova !== senhas.repetir ? 'As senhas não conferem.' : undefined
            }
          />
        </div>
        <div className="mt-4">
          <Botao
            disabled={!senhaPronta}
            carregando={trocarSenha.isPending}
            onClick={() => trocarSenha.mutate()}
          >
            Trocar senha
          </Botao>
        </div>

        <div className="mt-5 flex items-center gap-3 border-t border-[color:var(--color-borda-suave)] pt-5">
          <span className="text-sm text-slate-700">Verificação em duas etapas</span>
          {eu.mfaAtivo ? (
            <Etiqueta tom="sucesso">ativa</Etiqueta>
          ) : eu.mfaObrigatorio ? (
            <Etiqueta tom="perigo">obrigatória para o seu papel — pendente</Etiqueta>
          ) : (
            <Etiqueta tom="atencao">não configurada</Etiqueta>
          )}
        </div>
        {!eu.mfaAtivo && (
          <p className="mt-2 text-xs text-slate-500">
            O cadastro do aplicativo autenticador é oferecido no login.
          </p>
        )}
      </Cartao>

      {/* --------------------------------------------------------- sessões */}
      <Cartao
        titulo={`Onde você está conectado (${sessoes?.dados.length ?? 0})`}
        descricao="Não reconhece um destes? Encerre e troque a senha."
      >
        {(sessoes?.dados.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma outra sessão aberta.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {sessoes!.dados.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-4 py-2.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-slate-700">{s.userAgent ?? 'Dispositivo desconhecido'}</p>
                  <p className="text-xs text-slate-400">
                    {s.ip ?? 'sem IP'} · desde {formatarData(s.criadoEm)}
                  </p>
                </div>
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  carregando={encerrarSessao.isPending}
                  onClick={() => encerrarSessao.mutate(s.id)}
                >
                  Encerrar
                </Botao>
              </li>
            ))}
          </ul>
        )}
      </Cartao>

      <p className="text-center text-xs text-slate-400">
        Entrou como {sessao?.nome ?? iniciaisDe(eu.nome)} · último acesso{' '}
        {eu.ultimoLoginEm ? formatarData(eu.ultimoLoginEm) : '—'}
      </p>
    </div>
  )
}
